/**
 * Das Brett einer laufenden Partie — §67 Schritte 19 bis 23.
 *
 * **Zwei Zustände, ein Bildschirm.** In der Aufstellung ist das Brett ein Bedienelement: eigene
 * Figuren setzen, versetzen, zurücknehmen, bestätigen. Danach ist es eine Anzeige — und zeigt
 * zum ersten Mal **alle** Figuren, weil die Projektion ab `PLAYING` nicht mehr filtert (Schritt
 * 23: „alle Aufstellungen werden gleichzeitig sichtbar").
 *
 * **Verdeckt heißt verdeckt — und zwar zweifach** (seit `R17`, Nr. 55; bis dahin geheim). Während
 * der Aufstellung enthält der Zustand, den dieser Client bekommt, die fremden Figuren **nur ohne
 * Art** (`coveredPieces`): das entscheidet die Projektion im Server (**E134**), und sie ist die
 * Autorität. Diese Komponente zeichnet sie als Rückseite einer Scheibe und offene Figuren in
 * `SETUP` trotzdem **nur eigene** — als zweite Linie, genau wie `realtime/view.ts` ein `STATE` für
 * einen anderen Betrachter verwirft. Ab `PLAYING` fällt der Filter weg, und alle Figuren erscheinen
 * offen.
 *
 * **Vom Gegner sieht man außerdem eine Zahl** (Nr. 35): wie viele Figuren stehen und ob er
 * bestätigt hat. Das kommt aus der `SETUP`-Nachricht und nicht aus dem Brett.
 *
 * **Der Client schickt Absichten, keine Zustände** (Regel 1). `PLACE_PIECE` trägt Figur und Ziel
 * — kein `from`: woher die Figur kommt, weiß der Server. Setzen und Umsetzen sind dieselbe
 * Absicht (Nr. 32), es gibt hier also keinen zweiten Weg für „verschieben".
 *
 * **Die Frist wird angezeigt, nicht angewandt.** Läuft sie ab, verteilt der **Server** die
 * fehlenden Figuren (§11) und schickt einen neuen Zustand. Dieser Countdown rechnet mit der Uhr
 * des Browsers und ist deshalb ein Hinweis, keine Autorität — er löst nichts aus.
 *
 * **Die Feldkoordinaten gehören der Partie** (§42, `ARCHITECTURE.md` **E30**): der Host schaltet
 * sie beim Erstellen ein, und hierher kommen sie mit der Lobby-Ansicht. Fehlt die, bleiben sie
 * aus — der Default aus `RULE_CONFIG.md`, nicht die freundlichere Annahme.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ClientMessage, LobbyBotDto } from '@flaghex/contracts';
import {
  allPieces,
  capturedOf,
  createBoard,
  createSectorLayout,
  hexEquals,
  playerOf,
  type Hex,
  type PieceType,
} from '@flaghex/engine';

import { BoardSvg, type BoardPiece, type CoveredBoardPiece } from '../board/BoardSvg.js';
import {
  NO_ZOOM,
  boardViewOf,
  panned,
  withScale,
  type BoardZoom,
} from '../board/geometry.js';
import type { GameView } from '../realtime/view.js';
import { ConfirmButton } from './ConfirmButton.js';
import { readCoordinatesChoice, writeCoordinatesChoice } from './coordinates.js';
import { ResultCard, ResultLine } from './ResultCard.js';
import { resultOf } from './result.js';
import { fallbacksOf, historyOf } from './history.js';
import { HistoryPanel } from './HistoryPanel.js';
import { pieceInfoOf } from './pieceInfo.js';
import { PieceInfoPanel } from './PieceInfoPanel.js';
import { previewFigures } from './preview.js';
import { setupOf } from './setup.js';
import { formatDuration, statusOf } from './status.js';
import { StatusPanel } from './StatusPanel.js';
import { TurnControls } from './TurnControls.js';
import { actionsAt, actionsOfPiece, highlightsOf, turnOf } from './turn.js';

export interface GamePanelProps {
  readonly view: GameView;
  readonly send: (message: ClientMessage) => string | undefined;
  /**
   * Nur für den Host: die Partie beenden, für alle (`R12`). Ohne den Rückruf gibt es den Knopf
   * nicht — die Berechtigung prüft trotzdem der Dienst (Regel 1).
   */
  readonly onAbort?: (() => void) | undefined;
}

/** Was gerade gewählt ist: eine Figur aus dem Vorrat oder eine, die schon steht. */
type Choice = { readonly kind: 'pool' | 'board'; readonly pieceId: string } | undefined;

/**
 * Wie lange eine Erwägung zu sehen gewesen sein muss, bevor ein Klick auf sie bestätigt (R7).
 *
 * **300 ms**, und das ist eine Zeitspanne mit einem Grund: ein Doppelklick liegt
 * betriebssystemseitig bei etwa 500 ms, und seine zweite Hälfte trifft die Erwägung, kaum dass
 * sie da ist. Wer dagegen hinsieht, was er erwogen hat, und dann noch einmal tippt, braucht
 * länger. Kürzer ließe den Doppelklick durch, deutlich länger fühlte sich wie eine Hängerei an.
 */
const COMMIT_GUARD_MS = 300;

export function GamePanel({ view, send, onAbort }: GamePanelProps) {
  const { t } = useTranslation(['setup', 'play', 'pieces', 'pieceShort', 'status', 'common']);
  const [choice, setChoice] = useState<Choice>(undefined);
  const [now, setNow] = useState(() => Date.now());
  /**
   * Ausschnitt und Maßstab des Bretts (§64) — und welcher Bereich der Spalte gerade gewählt ist.
   *
   * **Beides gehört zur Ansicht, nicht zum Brett und nicht zur Partie.** Ein Zoom überlebt keinen
   * Neustart und gehört in keine Nachricht; er ist dasselbe wie die Scrollposition.
   */
  const [zoom, setZoom] = useState<BoardZoom>(NO_ZOOM);
  const [tab, setTab] = useState<string>('turn');

  const state = view.state;
  const me = view.playerId;
  /**
   * Die Feldkoordinaten (`R14`): der Haken des Hosts ist der **Startwert**, jeder schaltet für
   * sich um. Ohne Lobby-Ansicht gilt **aus** (fail closed, **E30**).
   */
  const hostCoordinates = view.lobby?.settings.showCoordinates ?? false;
  const [ownCoordinates, setOwnCoordinates] = useState<boolean | undefined>(() =>
    view.gameId === undefined ? undefined : readCoordinatesChoice(view.gameId),
  );
  useEffect(() => {
    setOwnCoordinates(view.gameId === undefined ? undefined : readCoordinatesChoice(view.gameId));
  }, [view.gameId]);
  const showCoordinates = ownCoordinates ?? hostCoordinates;
  const toggleCoordinates = (): void => {
    const next = !showCoordinates;
    setOwnCoordinates(next);
    if (view.gameId !== undefined) writeCoordinatesChoice(view.gameId, next);
  };
  /**
   * **Wessen Farbe unten liegt** — die Blickrichtung des Bretts (Zyklus R4).
   *
   * Aus dem **Betrachter**, nicht aus dem Zustand: dieselbe Partie sieht für jeden Spieler
   * anders aus, und das ist der ganze Zweck von R4. `undefined`, solange die Partie noch keine
   * Spieler trägt — das Brett bleibt dann in der kanonischen Lage.
   */
  const viewSector = view.state?.players.find((player) => player.id === me)?.sector;

  const deadlineAt = view.setup?.deadlineAt ?? null;
  useEffect(() => {
    if (deadlineAt === null) {
      return undefined;
    }
    const handle = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(handle);
  }, [deadlineAt]);

  // Die Figurenfarbe folgt ihrem Besitzer, nicht dem Feld, auf dem sie steht. In `SETUP` bleibt
  // Fremdes draußen — zweite Linie hinter der Projektion, siehe Modulkopf.
  const pieces = useMemo<readonly BoardPiece[]>(() => {
    if (state === undefined) return [];
    const visible = allPieces(state.boardState).filter(
      (piece) => state.status !== 'SETUP' || piece.owner === me,
    );
    return visible.map((piece) => ({
      id: piece.id,
      type: piece.type,
      owner: piece.owner,
      sector: playerOf(state, piece.owner)?.sector ?? 'green',
      at: piece.at,
    }));
  }, [state, me]);

  // Die verdeckten Figuren der anderen (`R17`): nur in `SETUP`, nur aus der Projektion, in der
  // Farbe ihres Besitzers.
  const covered = useMemo<readonly CoveredBoardPiece[]>(() => {
    if (state === undefined || state.status !== 'SETUP') return [];
    return (state.coveredPieces ?? [])
      .filter((piece) => piece.owner !== me)
      .map((piece) => ({
        id: piece.id,
        owner: piece.owner,
        sector: playerOf(state, piece.owner)?.sector ?? 'green',
        at: piece.at,
      }));
  }, [state, me]);

  /**
   * Die Anzeigenamen, aus der Lobby-Ansicht.
   *
   * Die `SETUP`-Nachricht trägt nur Spieler-**IDs** — und zwar zu Recht: sie geht nach jeder
   * Platzierung heraus, Namen ändern sich dabei nie. Beides zusammenzuführen ist Sache der
   * Oberfläche. Fehlt die Lobby-Ansicht doch einmal, bleibt die ID stehen: eine Kennung ist
   * verständlicher als eine leere Zeile.
   */
  const names = useMemo<ReadonlyMap<string, string>>(() => {
    const entries = new Map<string, string>();
    for (const slot of view.lobby?.slots ?? []) {
      if (slot.player !== undefined) entries.set(slot.player.playerId, slot.player.displayName);
    }
    for (const player of view.lobby?.waiting ?? []) {
      entries.set(player.playerId, player.displayName);
    }
    return entries;
  }, [view.lobby]);

  /**
   * **Welcher** Bot auf welchem Platz steht (`L3a`, **E104** Teilfrage 3) — aus der Lobby-Ansicht,
   * **wie die Namen**: der Zustand trägt keine Strategie (E53). Nach dem Start schickt der Dienst
   * sie neu, mit dem aufgelösten `stark` (**E107**). **Ob** ein Platz ein Bot ist, sagt weiter der
   * Zustand (`bots` unten) — diese Angabe beschreibt nur.
   */
  const botDescriptions = useMemo<ReadonlyMap<string, LobbyBotDto>>(() => {
    const entries = new Map<string, LobbyBotDto>();
    for (const player of [
      ...(view.lobby?.slots ?? []).flatMap((slot) => (slot.player === undefined ? [] : [slot.player])),
      ...(view.lobby?.waiting ?? []),
    ]) {
      if (player.bot !== undefined) entries.set(player.playerId, player.bot);
    }
    return entries;
  }, [view.lobby]);

  /**
   * Wer von einem Bot gesteuert wird (**E64**, Antwort 4).
   *
   * **Aus dem Zustand, nicht aus der Lobby-Ansicht.** Beide tragen die Angabe seit `V2-2f1`,
   * aber der Zustand ist hier immer da — die Lobby-Ansicht kann fehlen (ein Spieler, der nach
   * einem Neuladen nur den Zustand bekommt). Die Namen kommen aus der Lobby, weil es sie nur
   * dort gibt; die Steuerung nicht.
   */
  const bots = useMemo<ReadonlySet<string>>(
    () =>
      new Set(
        (state?.players ?? [])
          .filter((player) => player.playerType === 'BOT')
          .map((player) => player.id),
      ),
    [state],
  );

  const setup = useMemo(
    () => (state === undefined || me === undefined ? undefined : setupOf(state, me)),
    [state, me],
  );

  const turn = useMemo(
    () => (state === undefined || me === undefined ? undefined : turnOf(state, me)),
    [state, me],
  );

  /**
   * Das Sektor-Layout dieser Partie.
   *
   * Einmal je Brettgröße und nicht je Zeichnen: es entstehen dabei 217 Felder. Gebraucht wird es
   * für die **kanonischen** Feld-IDs in der Zugliste (`green-C3`) — dieselbe Benennung wie in der
   * Referenzgrafik und im Event-Log.
   */
  const layout = useMemo(
    () => createSectorLayout(createBoard({ sideLength: state?.ruleConfig.board.sideLength ?? 9 })),
    [state?.ruleConfig.board.sideLength],
  );

  /**
   * Die Figurenklasse je Figuren-ID, über **alle** Figuren der Partie.
   *
   * Eine Preview nennt auch fremde Figuren („Anna schlägt deinen Läufer"), und die stehen nicht
   * in der eigenen Zugsicht. Gesammelt wird vom Brett **und** aus den Vorräten aller Spieler —
   * ein Respawn betrifft eine Figur, die auf keinem Feld steht.
   *
   * **Sie wird gesammelt und nie geleert** (Befund der Sichtprüfung zu Zyklus 11d). Der Grund
   * ist die Pause: in `PAUSED` filtert die Projektion das **gesamte** Brett heraus (Nr. 26),
   * und ein Gedächtnis nur über den aktuellen Zustand wäre dann leer — in der Historie stand
   * `p1-guard-1` statt „Wächter", in jeder Zeile. Dasselbe gilt für eine Figur, die vom Brett
   * **verschwunden** ist: ein Stoß über den Rand (§18) entfernt sie ganz, und ihr Name in der
   * Zeile darüber soll bleiben.
   *
   * **Kein Geheimnisbruch.** Hier landet nur, was dieser Zuschauer ohnehin bekommen hat: in
   * `SETUP` enthält seine Projektion fremde Figuren nicht (§45), also auch dieses Gedächtnis
   * nicht. Und eine Figurenklasse ist ab `PLAYING` öffentlich.
   */
  const knownTypes = useRef(new Map<string, PieceType>());
  /**
   * Dasselbe Gedächtnis für den **Besitzer** (Befund B6, Zyklus R6).
   *
   * Gebraucht seit **R2**: der Stoßer stößt jede Figur, auch die eigene — wem das Opfer gehörte,
   * ist aus der Zeile sonst nicht mehr abzulesen. In der **Historie** steht es im Ereignis
   * (`victimOwner`); die **Aktionsliste** zeigt dagegen eine noch nicht ausgeführte Aktion, deren
   * Opfer auf dem Brett steht — dort genügt diese Karte. Dieselbe Begründung für das Gedächtnis
   * wie oben, und derselbe Geheimnisschutz: hier landet nur, was dieser Zuschauer ohnehin hat.
   */
  const knownOwners = useRef(new Map<string, string>());

  /**
   * **Seit wann diese Erwägung zu sehen ist** — die Sperre gegen den versehentlichen Doppelklick.
   *
   * Der zweite Klick bestätigt (R7), und damit wird eine Doppelklick-Geste zu einem Zug. Gemessen
   * wird deshalb nicht der Abstand zwischen den Klicks, sondern das **Alter der Erwägung**: beim
   * zweiten Klick eines Doppelklicks ist sie gerade erst vom Server zurückgekommen. Ein bewusstes
   * zweites Antippen ist langsamer, und über eine langsame Leitung ist sie beim zweiten Klick
   * noch gar nicht da — dann passiert ohnehin nichts.
   *
   * `Date.now()` ist hier erlaubt: Regel 4 bindet die **Engine**, nicht die Darstellung.
   */
  const previewSeenAt = useRef<{ readonly id: string; readonly at: number } | undefined>(undefined);
  useEffect(() => {
    if (view.preview !== null) {
      previewSeenAt.current = { id: view.preview.id, at: Date.now() };
    }
  }, [view.preview?.id, view.preview]);
  const pieceTypes = useMemo<ReadonlyMap<string, PieceType>>(() => {
    const types = knownTypes.current;
    if (state !== undefined) {
      for (const piece of allPieces(state.boardState)) types.set(piece.id, piece.type);
      for (const player of state.players) {
        for (const piece of capturedOf(state.boardState, player.id)) {
          types.set(piece.id, piece.type);
        }
      }
    }
    // Eine Kopie: eine Map, die ihre Identität behält, würde `useMemo` bei den Empfängern nicht
    // auffrischen — und dann bliebe eine neue Figur ohne Namen.
    return new Map(types);
  }, [state]);

  const pieceOwners = useMemo<ReadonlyMap<string, string>>(() => {
    const owners = knownOwners.current;
    if (state !== undefined) {
      for (const piece of allPieces(state.boardState)) owners.set(piece.id, piece.owner);
      for (const player of state.players) {
        for (const piece of capturedOf(state.boardState, player.id)) {
          owners.set(piece.id, player.id);
        }
      }
    }
    return new Map(owners);
  }, [state]);

  /**
   * Wann die zuletzt empfangene `CLOCK`-Nachricht eintraf — **lokal** gemessen.
   *
   * Die Nachricht nennt den Serverzeitpunkt `at`, zu dem ihre Werte galten. Weitergezählt wird
   * aber nicht mit einem Vergleich von Serveruhr und Browseruhr, sondern mit der **Spanne**
   * seit dem Eintreffen: ein Versatz zwischen beiden Uhren ist real und manchmal groß (898
   * Sekunden zwischen Container- und Prozessuhr, gemessen in Zyklus 10f), und er würde die
   * Anzeige um genau diesen Betrag verfälschen.
   */
  const [clockStamp, setClockStamp] = useState<number | undefined>(undefined);
  const [clockTick, setClockTick] = useState(0);
  const clock = view.clock;
  useEffect(() => {
    setClockStamp(clock === undefined ? undefined : Date.now());
  }, [clock]);
  useEffect(() => {
    // Nur wenn eine Uhr läuft, muss überhaupt neu gezeichnet werden.
    if (clock?.running == null) return undefined;
    const handle = setInterval(() => setClockTick((value) => value + 1), 1000);
    return () => clearInterval(handle);
  }, [clock?.running]);

  /** Die Zughistorie (§56). Vollständig: `HISTORY` bringt sie beim Anmelden mit. */
  const history = useMemo(() => historyOf(view.events), [view.events]);

  /**
   * Die Rückfälle je Bot (`L3b`, **E112** Antwort 2) — aus **derselben** Historie wie die Zeilen,
   * damit ⓘ und Historie nie Verschiedenes zählen.
   */
  const fallbacks = useMemo(() => fallbacksOf(history), [history]);

  /** Das Ergebnis am Ende (`R13`) — `undefined`, solange die Partie läuft. */
  const result = useMemo(() => (state === undefined ? undefined : resultOf(state, me)), [state, me]);
  /** Ob die Karte über dem Brett weggeklickt ist. Gehört zur Ansicht, nicht zur Partie. */
  const [resultDismissed, setResultDismissed] = useState(false);
  /** Ein Spieler heißt beim Namen, eine Seite ist ein Team — wie im Statusbereich. */
  const sideLabel = (side: string): string => names.get(side) ?? t('play:teamName', { team: side });

  /** Der Statusbereich nach §49 — Punkte, Zeit, Vorrat, wer am Zug ist. */
  const status = useMemo(
    () =>
      state === undefined
        ? undefined
        : statusOf({
            state,
            names,
            bots: botDescriptions,
            fallbacks,
            connected: new Set(view.connectedPlayers),
            clock,
            elapsedSinceClockMs: clockStamp === undefined ? 0 : Date.now() - clockStamp,
            // Nr. 38: eine regelwidrig erzeugte Partie muss man ihr ansehen (Zyklus 11g).
            ruleOverridden: view.ruleOverridden,
          }),
    // `clockTick` **muss** in der Liste stehen: der Taktgeber zeichnet zwar neu, aber eine
    // gemerkte Ableitung mit unveränderten Abhängigkeiten gibt den alten Wert zurück — die Uhr
    // stünde still und sähe dabei völlig richtig aus. Von einem Test gefunden, der nach drei
    // Sekunden nachsieht.
    [state, names, botDescriptions, fallbacks, view.connectedPlayers, clock, clockStamp, clockTick],
  );

  /**
   * Die Information zur gewählten Figur (§51).
   *
   * „Beim Auswählen" — und ausgewählt wird in **beiden** Phasen: in der Aufstellung eine Figur
   * aus dem Vorrat, im Zug eine vom Brett. Dieselbe Anzeige für beides, weil die Angaben an der
   * Klasse hängen und nicht an der Position.
   */
  const chosenInfo = useMemo(
    () =>
      state === undefined || choice === undefined ? undefined : pieceInfoOf(state, choice.pieceId),
    [state, choice],
  );

  /** Die erwogene Stellung (§36) — bei allen sichtbar, nicht nur beim Spieler am Zug. */
  const previewed = useMemo(
    () => (state === undefined ? undefined : previewFigures(view.preview, state)),
    [view.preview, state],
  );

  /**
   * Ein Zugwechsel löscht die Auswahl.
   *
   * Sonst bliebe die Figur des vorigen Zuges gewählt, und das Brett hübe Felder hervor, die zu
   * einer Stellung gehören, die es nicht mehr gibt. Gehorcht wird dabei dem **Zustand** und nicht
   * dem eigenen Klick: auch ein Commit eines anderen Spielers beendet den Zug.
   */
  const turnNumber = state?.turnNumber;
  const activePlayer = state?.activePlayer;
  useEffect(() => {
    setChoice(undefined);
  }, [turnNumber, activePlayer]);

  if (state === undefined || me === undefined) {
    return <p role="status">{t('common:loading')}</p>;
  }

  const inSetup = state.status === 'SETUP';

  /**
   * Der Satz unter dem Brett — **er erklärt, warum das Brett so aussieht** (Befund B3, R5).
   *
   * Bis R5 stand hier ein einziger Satz in jedem Zustand außer `SETUP`, und er war in zwei
   * davon falsch. In `PAUSED` sogar das Gegenteil des Sichtbaren: die Projektion verdeckt dort
   * das **gesamte** Brett für alle (Nr. 26, „die Pause darf keine Analysezeit sein") — der
   * Spieler sah ein leeres Brett und darunter „jetzt sind alle Figuren sichtbar". In
   * `FINISHED` fehlen die Figuren eines durch Zeitablauf Ausgeschiedenen (Nr. 3).
   *
   * Nach dem Partieende gibt es nichts zu erklären; dort steht deshalb nichts. Dasselbe in der
   * Aufstellung — dass man nur die eigenen Figuren sieht (§10), sagt der Aufstellungsbereich.
   */
  const boardCaption =
    state.status === 'PLAYING'
      ? t('play:revealed')
      : state.status === 'PAUSED'
        ? t('play:boardHidden')
        : null;

  /**
   * Die Bereiche der Spalte — und damit zugleich die Reiter der Schmalform.
   *
   * **Eine Liste für beides.** Ein Reiter ohne Bereich wäre eine Sackgasse, ein Bereich ohne
   * Reiter auf einem schmalen Bildschirm unerreichbar; aus zwei Listen würde früher oder später
   * das eine ohne das andere.
   */
  const panels: readonly string[] = inSetup
    ? ['setup', 'piece']
    : state.status === 'FINISHED'
      ? ['piece', 'status', 'history']
      : ['turn', 'piece', 'status', 'history'];
  // Wechselt die Phase, gilt wieder der erste Bereich — der gewählte kann weg sein.
  const activeTab = panels.includes(tab) ? tab : (panels[0] ?? 'piece');

  /** Der Ausgangsausschnitt: dieselbe Rechnung wie im Brett, aus derselben Funktion. */
  const baseView = boardViewOf(state.ruleConfig.board.sideLength);
  const ZOOM_STEP = 1.5;
  const editable = inSetup && setup !== undefined && !setup.confirmed;
  const selectedPiece =
    choice === undefined ? undefined : setup?.placed.find((piece) => piece.id === choice.pieceId);

  const place = (cell: Hex): void => {
    if (!editable || choice === undefined) return;
    if (!(setup?.free ?? []).some((free) => hexEquals(free, cell))) return;
    send({ type: 'PLACE_PIECE', pieceId: choice.pieceId, to: cell });
    setChoice(undefined);
  };

  const onSelectInSetup = (cell: Hex): void => {
    if (!editable) return;
    const own = setup?.placed.find((piece) => hexEquals(piece.at, cell));
    if (own !== undefined) {
      // Eine eigene Figur anklicken heißt: sie wählen — zum Versetzen oder Zurücknehmen.
      setChoice({ kind: 'board', pieceId: own.id });
      return;
    }
    place(cell);
  };

  /** Die Aktionen der gewählten Figur — leer, solange keine gewählt ist oder niemand am Zug. */
  const chosenActions =
    turn === undefined || choice === undefined ? [] : actionsOfPiece(turn, choice.pieceId);

  /**
   * Ein Klick im Zug: eine eigene Figur wählen, oder auf einem hervorgehobenen Feld die Aktion
   * dorthin erwägen.
   *
   * **Hinausgeschickt wird die Action ID**, nicht das Feld (§74, Regel 1). Liegt auf dem Feld
   * keine Aktion, passiert nichts — der Server müsste sie abweisen, und ein Klick ins Leere soll
   * keine Absicht erzeugen.
   *
   * **Die Prüfung auf das Zugrecht ist eine zweite Linie und heute unerreichbar:** ohne Zugrecht
   * bekommt das Brett gar keinen `onSelect` und ist damit kein Bedienelement. Die Gegenprobe
   * dieses Zyklus hat das belegt — sie zu entfernen ließ alle Tests grün. Sie bleibt trotzdem
   * stehen, weil sie dieselbe Regel umsetzt wie die Bedingung am Brett (Nr. 36) und weil die
   * Aufstellung es genauso hält; wer künftig `onSelect` unbedingt übergibt, läuft nicht in eine
   * Absicht, die der Server abweisen muss.
   */
  const onSelectInTurn = (cell: Hex): void => {
    if (turn === undefined || !turn.active) return;

    /*
      **Der zweite Klick auf dasselbe Feld bestätigt** (Zyklus R7, vom User beauftragt).

      Bis dahin war der Weg dreistufig und endete **rechts oben** am Knopf; der zweite Klick auf
      dasselbe Feld schickte nur dieselbe Erwägung noch einmal. Die Erwägungsphase bleibt dabei
      unangetastet: der erste Klick erwägt, der zweite bestätigt — zwei eigene Akte, wie §33 und
      §36 sie verlangen. Ein Klick auf ein **anderes** Feld ersetzt weiterhin (§31), und der
      Knopf rechts bleibt, wo er ist.

      **Bestätigt wird die Erwägung, nicht die lokal gefundene Aktion** — dieselbe Vorsicht wie
      im Knopf: `COMMIT` nennt die ID der Preview. Lägen auf einem Feld zwei Aktionen, wäre
      sonst nicht gesagt, welche gemeint ist.
    */
    const erwogen = view.preview;
    if (erwogen !== null && erwogen.player === me && hexEquals(erwogen.to, cell)) {
      if (previewSeenAt.current?.id === erwogen.id && Date.now() - previewSeenAt.current.at >= COMMIT_GUARD_MS) {
        send({ type: 'COMMIT', actionId: erwogen.id });
      }
      // Auch eine abgewiesene Bestätigung endet hier: ein erneutes `SET_PREVIEW` auf dieselbe
      // Aktion wäre eine Nachricht ohne Wirkung — und Preview-Spam ist ein echter Vektor.
      return;
    }

    const action = actionsAt(chosenActions, cell)[0];
    if (action !== undefined) {
      send({ type: 'SET_PREVIEW', actionId: action.id });
      return;
    }

    const own = turn.placed.find((piece) => hexEquals(piece.at, cell));
    if (own !== undefined) {
      setChoice({ kind: 'board', pieceId: own.id });
    }
  };

  /** Das Feld der gewählten Figur — eine Vorratsfigur steht auf keinem. */
  const chosenCell =
    turn === undefined || choice === undefined
      ? undefined
      : turn.placed.find((piece) => piece.id === choice.pieceId)?.at;

  const statusLine = (): string => {
    if (state.status === 'PAUSED') return t('play:paused');
    if (state.status !== 'PLAYING') return t('play:over');
    const active = state.activePlayer;
    if (active === me) return t('play:yourTurn', { turn: state.turnNumber });
    return t('play:turnOf', {
      turn: state.turnNumber,
      name: active === undefined ? '' : (names.get(active) ?? active),
    });
  };

  return (
    <main data-screen="game">
      {/*
        Die Kopfleiste trägt, was sich nicht verschieben darf, wenn darunter etwas wächst: in
        welcher Phase die Partie ist und wer am Zug ist. §49 nennt „aktiver Spieler muss klar
        erkennbar sein" als eigenen Satz — vorher stand diese Zeile als Absatz zwischen
        Überschrift und Brett und rutschte mit jedem Bereich darunter weiter nach oben.
      */}
      <div data-region="bar">
        <h1>{inSetup ? t('setup:heading') : t('play:heading')}</h1>
        <p className="state" role="status">
          {t('lobby:statusLabel', { status: t(`status:${state.status}`) })}
        </p>
        <span className="spacer" />
        <p className="turnline" role="status">
          {inSetup ? t('setup:secret') : statusLine()}
        </p>
        {/* Ganz rechts in der Kopfleiste (`R12`, Entscheidung des Users): immer sichtbar, und
            er verschiebt sich nicht, wenn die Spalte wächst. */}
        {onAbort === undefined ||
        (state.status !== 'SETUP' && state.status !== 'PLAYING' && state.status !== 'PAUSED') ? null : (
          <ConfirmButton
            label={t('play:abort')}
            confirmLabel={t('play:abortConfirm')}
            onConfirm={onAbort}
          />
        )}
      </div>

      {/*
        Die Aufteilung „Werkbank" (Designphase D2, entschieden am 2026-10-05): Brett links,
        rechts **eine** Spalte in Bedienreihenfolge — was ich tun kann, welche Figur gewählt ist,
        wie es steht, was war. Vorher stand alles untereinander, und zwar die Bedienung **unter**
        der Historie: man bediente oben und las unten.
      */}
      {result === undefined || !resultDismissed ? null : (
        <ResultLine result={result} label={sideLabel} />
      )}
      <div data-layout="game">
        <div data-region="board">
          {/*
            Die Zoombedienung steht **am Brett** und nicht in der Spalte: sie gehört zu dem, was
            sie verändert. §64 verlangt Touch und verbietet Hover-only — es sind deshalb Knöpfe
            mit Beschriftung und keine Mausgesten.
          */}
          <div className="boardtools">
            <button
              type="button"
              data-zoom-out=""
              aria-label={t('board:zoomOut')}
              disabled={zoom.scale <= 1}
              onClick={() => setZoom((current) => withScale(baseView, current, current.scale / ZOOM_STEP))}
            >
              −
            </button>
            <span className="level">{t('board:zoomLevel', { percent: Math.round(zoom.scale * 100) })}</span>
            <button
              type="button"
              data-zoom-in=""
              aria-label={t('board:zoomIn')}
              onClick={() => setZoom((current) => withScale(baseView, current, current.scale * ZOOM_STEP))}
            >
              +
            </button>
            <button
              type="button"
              data-zoom-reset=""
              aria-label={t('board:zoomReset')}
              disabled={zoom.scale <= 1}
              onClick={() => setZoom(NO_ZOOM)}
            >
              {t('board:zoomReset')}
            </button>
            {/* Neben den Zoom-Knöpfen (`R14`, Entscheidung des Users): wirkt nur hier. */}
            <label className="coords">
              <input
                type="checkbox"
                data-coordinates-toggle=""
                checked={showCoordinates}
                onChange={toggleCoordinates}
              />{' '}
              {t('play:coordinates')}
            </label>
          </div>
          <div className="boardfit">
            <BoardSvg
              zoom={zoom}
              onPan={(dx, dy) => setZoom((current) => panned(baseView, current, dx, dy))}
        {...(viewSector === undefined ? {} : { viewSector })}
        sideLength={state.ruleConfig.board.sideLength}
        startZoneRows={state.ruleConfig.startZone.rows}
        showCoordinates={showCoordinates}
        pieces={pieces}
        {...(covered.length === 0 ? {} : { covered })}
        {...(previewed === undefined || previewed.ghosts.length === 0
          ? {}
          : { ghosts: previewed.ghosts })}
        {...(previewed === undefined || previewed.doomed.length === 0
          ? {}
          : { doomed: previewed.doomed })}
        {...(previewed === undefined || previewed.traces.length === 0
          ? {}
          : { traces: previewed.traces })}
        {...(inSetup
          ? {
              // In der Aufstellung gibt es nur eine Art: „hier darfst du setzen" (§10).
              ...(editable && choice !== undefined
                ? {
                    highlighted: (setup?.free ?? []).map((cell) => ({
                      at: cell,
                      kind: 'PLACE' as const,
                    })),
                  }
                : {}),
              ...(selectedPiece === undefined ? {} : { selected: selectedPiece.at }),
              ...(editable ? { onSelect: onSelectInSetup } : {}),
            }
          : {
              // Hervorhebungen nur am Zug (Nr. 36) — und nur für die gewählte Figur (§50).
              ...(chosenActions.length === 0 ? {} : { highlighted: highlightsOf(chosenActions) }),
              ...(chosenCell === undefined ? {} : { selected: chosenCell }),
              ...(turn?.active === true ? { onSelect: onSelectInTurn } : {}),
            })}
            />
          </div>
          {boardCaption === null ? null : <p className="caption">{boardCaption}</p>}
          {result === undefined || resultDismissed ? null : (
            <ResultCard
              result={result}
              label={sideLabel}
              onDismiss={() => setResultDismissed(true)}
            />
          )}
        </div>

        <div data-region="rail" data-tab={activeTab}>
          {/*
            Die Reiter sind **nur schmal** sichtbar; breit stehen alle Bereiche untereinander und
            das Stylesheet blendet sie aus. Gerendert werden sie immer — so hängt die Auswahl
            nicht an einer Fensterbreite, die React gar nicht kennt.
          */}
          <div className="tabs" role="tablist" aria-label={t('play:heading')}>
            {panels.map((id) => (
              <button
                key={id}
                type="button"
                role="tab"
                data-tab-button={id}
                aria-selected={activeTab === id}
                onClick={() => setTab(id)}
              >
                {t(`play:tab.${id}`)}
              </button>
            ))}
          </div>

          {/*
            Nach dem Partieende gibt es keine Bedienung mehr — keinen Zug, keine Erwägung, keinen
            Vorrat zum Einsetzen. Vorher stand unter „Die Partie ist beendet" weiter „Du bist
            nicht am Zug — zusehen, was der andere erwägt" (Sichtprüfung zu Zyklus 10i). Was
            bleibt, ist die Auskunft: Stand, Sieger und Historie.
          */}
          {inSetup || turn === undefined || state.status === 'FINISHED' ? null : (
            <section data-panel="turn">
              <TurnControls
                turn={turn}
                selectedPiece={choice?.pieceId}
                onSelectPiece={(pieceId) => setChoice({ kind: 'pool', pieceId })}
                preview={view.preview}
                me={me}
                sector={state.players.find((player) => player.id === me)?.sector ?? 'green'}
                layout={layout}
                pieceTypes={pieceTypes}
                pieceOwners={pieceOwners}
                names={names}
                send={send}
              />
            </section>
          )}

          {!inSetup || setup === undefined ? null : (
            <section data-panel="setup">
              <h3>{t('setup:pool')}</h3>
              <p className="lede">{t('setup:intro')}</p>
              {setup.confirmed ? (
                <p role="status">{t('setup:confirmed')}</p>
              ) : (
                <>
                  <p>{t('setup:selectHint')}</p>
                  <p>{t('setup:placedHint')}</p>
                </>
              )}

              {setup.pool.length === 0 ? (
                <p>{t('setup:poolEmpty')}</p>
              ) : (
                <ul>
                  {setup.pool.map((piece) => (
                    <li key={piece.id}>
                      <button
                        type="button"
                        data-pool-piece={piece.id}
                        disabled={!editable}
                        aria-pressed={choice?.pieceId === piece.id}
                        onClick={() => setChoice({ kind: 'pool', pieceId: piece.id })}
                      >
                        {t(`pieces:${piece.type}`)} ({t(`pieceShort:${piece.type}`)})
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {selectedPiece === undefined || !editable ? null : (
                <p>
                  <button
                    type="button"
                    onClick={() => {
                      send({ type: 'UNPLACE_PIECE', pieceId: selectedPiece.id });
                      setChoice(undefined);
                    }}
                  >
                    {t('setup:unplace')}
                  </button>
                </p>
              )}

              <p role="status">
                {deadlineAt === null
                  ? t('setup:deadlineNone')
                  : deadlineAt - now <= 0
                    ? t('setup:deadlineOver')
                    : t('setup:deadline', { time: formatDuration(deadlineAt - now) })}
              </p>

              {setup.confirmed ? null : (
                <p>
                  <button type="button" onClick={() => setup.complete && send({ type: 'CONFIRM_SETUP' })}>
                    {t('setup:confirm')}
                  </button>
                  {setup.complete ? null : ` ${t('setup:incomplete')}`}
                </p>
              )}

              <h3>{t('setup:progress')}</h3>
              <ul>
                {(view.setup?.progress ?? []).map((entry) => (
                  <li key={entry.player}>
                    <span>{names.get(entry.player) ?? entry.player}</span>
                    {' · '}
                    <span>
                      {t('setup:progressEntry', { placed: entry.placed, total: entry.total })}
                    </span>
                    {entry.confirmed ? (
                      <>
                        {' · '}
                        <span>{t('setup:progressConfirmed')}</span>
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/*
            Die Figureninformation steht **immer** da, auch ohne Auswahl (§51). Ein Bereich, der
            erscheint und verschwindet, verschiebt bei jedem Klick alles darunter — und genau
            darunter liegt der Stand, den man im Blick behalten will.
          */}
          <section data-panel="piece">
            <h3>{t('play:info.heading')}</h3>
            {chosenInfo === undefined ? (
              <p>{t('play:nothingSelected')}</p>
            ) : (
              <PieceInfoPanel info={chosenInfo} names={names} />
            )}
          </section>

          {inSetup || status === undefined ? null : <StatusPanel status={status} />}

          {inSetup ? null : (
            <HistoryPanel
              entries={history}
              names={names}
              pieceTypes={pieceTypes}
              layout={layout}
              bots={bots}
            />
          )}
        </div>
      </div>
    </main>
  );
}
